import React, { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import Loader from "../../Loader";
import showToast from "../../../helpers/showToast";
import { get, put } from "../../../server";
import { loggedInUser, teamIDCheck } from "../../../utils/store";
import {
  PRIORITY,
  TOGGLE_TEAM_FUNCTION_VISIBILITY,
} from "../../../utils/constants/apiUrl";
import cardIcon from "../../../assets/images/function_page_icons/cardIcon.svg";
import paymentIcon from "../../../assets/images/function_page_icons/paymentIcon.svg";
import emergencyIcon from "../../../assets/images/function_page_icons/emergencyIcon.svg";
import customIcon from "../../../assets/images/function_page_icons/customIcon.svg";
import fileUploadIcon from "../../../assets/images/function_page_icons/fileUploadIcon.svg";
import formbuilderIcon from "../../../assets/images/function_page_icons/formbuilderIcon.svg";
import memoryIcon from "../../../assets/images/function_page_icons/memoryIcon.svg";
import medicalIcon from "../../../assets/images/function_page_icons/medicalIcon.svg";

const functions = [
  ["card", "Digital Card", "Create Your Personal & Business Card", cardIcon],
  ["payment", "Payment Service", "Link Your Payment Service Account", paymentIcon],
  ["emergency_contact", "Emergency Contact", "For Emergency Purpose", emergencyIcon],
  ["custom_url", "Custom URL's", "Create Your Custom URL's", customIcon],
  ["file_upload", "File Upload URL", "Upload and Share documents", fileUploadIcon],
  ["form_builder", "Lead Builder", "For Lead Generation", formbuilderIcon],
  ["memory", "Memory", "Create Your Audio & video Link", memoryIcon],
  ["medical_id", "Medical ID", "Create Your Medical ID", medicalIcon],
];

const TeamFunctionVisibility = () => {
  const loggedUser = useAtomValue(loggedInUser);
  const selectedTeamId = useAtomValue(teamIDCheck);
  const teamId =
    selectedTeamId ||
    loggedUser?.teams?.id ||
    loggedUser?.secret_code?.find((item) => item.team_id)?.team_id ||
    loggedUser?.connected_teams?.[0]?.id;
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!teamId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await get(
        `${PRIORITY}?team_id=${teamId}`,
        loggedUser?.token || null
      );
      if (response?.status) {
        const settings = Array.isArray(response.data) ? response.data : [];
        setVisibility(
          Object.fromEntries(
            functions.map(([functionType]) => {
              const setting = settings.find(
                (item) => item.function_type === functionType
              );
              return [
                functionType,
                setting ? Number(setting.is_member_visible) === 1 : true,
              ];
            })
          )
        );
      } else {
        setVisibility(
          Object.fromEntries(functions.map(([functionType]) => [functionType, true]))
        );
        showToast(false, response?.message || "Function settings could not be loaded.");
      }
      setLoading(false);
    };

    loadSettings();
  }, [loggedUser?.token, teamId]);

  const toggleVisibility = async (functionType) => {
    if (!teamId || updating) return;
    const nextValue = !visibility[functionType];
    setUpdating(functionType);
    setVisibility((current) => ({ ...current, [functionType]: nextValue }));

    const response = await put(
      TOGGLE_TEAM_FUNCTION_VISIBILITY,
      {
        team_id: Number(teamId),
        function_type: functionType,
        is_member_visible: nextValue ? 1 : 0,
      },
      loggedUser?.token || null
    );

    if (!response?.status) {
      setVisibility((current) => ({ ...current, [functionType]: !nextValue }));
      showToast(false, response?.message || "Function visibility could not be updated.");
    }
    setUpdating(null);
  };

  if (loading) return <Loader />;

  if (!teamId) {
    return <div className="alert alert-warning">No active team was found.</div>;
  }

  return (
    <div className="row g-3 mt-1">
      {functions.map(([functionType, name, description, icon]) => (
        <div className="col-12 col-md-6" key={functionType}>
          <div
            className="d-flex align-items-center justify-content-between h-100 p-3 border rounded bg-white"
            style={{ minHeight: 88 }}
          >
            <div className="d-flex align-items-center pe-3">
              <img
                alt=""
                className="flex-shrink-0 me-3"
                src={icon}
                style={{ height: 48, objectFit: "contain", width: 48 }}
              />
              <div>
                <h5 className="mb-1">{name}</h5>
                <p className="mb-0 text-muted">{description}</p>
              </div>
            </div>
            <div className="form-check form-switch flex-shrink-0 mb-0">
              <input
                aria-label={`${visibility[functionType] ? "Disable" : "Enable"} ${name} for team members`}
                checked={visibility[functionType] ?? true}
                className="form-check-input"
                disabled={Boolean(updating)}
                onChange={() => toggleVisibility(functionType)}
                role="switch"
                type="checkbox"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TeamFunctionVisibility;
